import { getUpgradeEntryPath } from '@shared/components/organization/billing/upgradeNavigation';
import { getOrganizationIcon } from '@shared/constants/organization/OrganizationValues';
import { useCurrentUserStore } from '@shared/hooks/state/CurrentUserContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import { IMenuItem, ISubMenuItem } from '@shared/models/components/IMenuProps';
import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// TODO ADMIN PANEL: Update support link or look at using a landing page that navigates them to the right kind of support. Think SalesForce
export default function useAdminMenuItems() {
    const { t } = useTranslation();
    const memberships = useOrganizationStore((state) => state.memberships);
    const isLicenseExpired = useCurrentUserStore((state) => state.isLicenseExpired);
    const [topItems, setTopItems] = useState<IMenuItem[]>([]);
    const [bottomItems, setBottomItems] = useState<IMenuItem[]>([]);

    const getSubmenuItems = useCallback(
        (membership: OrganizationMemberDetailsModel) => {
            let subMenu: ISubMenuItem[] = [];
            const isOrgMultipleMembers = (membership.organization.maxMembers ?? 0) > 1;
            const originatedByOrganizationId = membership.organization.originatedByOrganizationId;
            const isManagedOrganization =
                originatedByOrganizationId != undefined &&
                originatedByOrganizationId != null &&
                originatedByOrganizationId.length > 0;

            // User is one of many members of an organization
            if (isOrgMultipleMembers || isManagedOrganization) {
                subMenu = [
                    ...(membership.canViewMembers || membership.isAdmin
                        ? [
                              {
                                  label: t('common:organization.member.titlePlural'),
                                  href: `org/${membership.organization.id}/members`,
                                  activeStateMatchCriteria: 'end',
                              } as ISubMenuItem,
                          ]
                        : []),
                    ...(membership.canInviteMembers || membership.isAdmin
                        ? [
                              {
                                  label: t('common:organization.invite.titlePlural'),
                                  href: `org/${membership.organization.id}/members/invites`,
                                  activeStateMatchCriteria: 'end',
                              } as ISubMenuItem,
                          ]
                        : []),
                    // {
                    //     label: t('common:organization.metrics.title'),
                    //     href: `org/${membership.organization.id}/metrics`,
                    // },

                    ...(membership.canAccessOrganizationProfiles ||
                    membership.canEditOrganizationProfiles ||
                    membership.isAdmin
                        ? [
                              {
                                  label: t('common:organization.profiles.titleShort'),
                                  href: `org/${membership.organization.id}/profiles`,
                              } as ISubMenuItem,
                          ]
                        : []),

                    ...(membership.organization.isReseller &&
                    (membership.canManageChildOrganizations || membership.isAdmin)
                        ? [
                              {
                                  label: t('common:organization.managed.title'),
                                  href: `org/${membership.organization.id}/managed`,
                              },
                              {
                                  label: t('common:license.key.title'),
                                  href: `org/${membership.organization.id}/licensekey`,
                              },
                              {
                                  label: t('common:license.key.product.title'),
                                  href: `org/${membership.organization.id}/purchase`,
                              },
                          ]
                        : []),
                    ...(membership.canCreateAuthClients || membership.canViewAuthClients || membership.isAdmin
                        ? [
                              {
                                  label: t('common:organization.projects.title'),
                                  href: `org/${membership.organization.id}/projects`,
                              } as ISubMenuItem,
                          ]
                        : []),
                    ...(membership.organization.isEmployeeAccess &&
                    (membership.canManageGlobalAutomationRules || membership.isAdmin)
                        ? [
                              {
                                  label: t('common:organization.governance.title'),
                                  href: `org/${membership.organization.id}/governance`,
                              } as ISubMenuItem,
                          ]
                        : []),
                    {
                        label: t('common:settings.title'),
                        href: `org/${membership.organization.id}/settings`,
                    },
                ];
            } else {
                // User is the only member of a single organization -> show direct billing
                subMenu = [
                    {
                        label: isLicenseExpired
                            ? t('common:upgrade')
                            : t('common:license.key.product.Subscription.title'),
                        href: getUpgradeEntryPath({ organizationId: membership.organizationId }),
                    },
                ];
            }

            return subMenu;
        },
        [isLicenseExpired, t]
    );

    // Helper to generate organization menu items
    const generateOrgItems = useCallback((): IMenuItem[] => {
        if (memberships.length === 0 || !useCurrentUserStore.getState().isEmailVerified) return [];

        return memberships.map<IMenuItem>((membership) => ({
            icon: getOrganizationIcon(membership.organization),
            label: membership.organization.name,
            href: `org/${membership.organization.id}`,
            submenu: getSubmenuItems(membership),
        }));
    }, [memberships, getSubmenuItems]);

    // Generate default top menu items
    const generateDefaultItems = useCallback(
        (): IMenuItem[] => [
            {
                icon: 'squaredMenu',
                label: t('common:dashboard.title'),
                href: '/',
            },
        ],
        [t]
    );

    // Generate bottom menu items
    const generateBottomItems = useCallback(
        (): IMenuItem[] => [
            {
                icon: 'code',
                label: t('common:developer.title'),
                href: '/developer',
            },
            {
                icon: 'help',
                label: t('common:support.title'),
                href: 'https://cephable.com/free-trial/support/',
            },
        ],
        [t]
    );

    // Consolidated effect to set menu items
    useEffect(() => {
        setTopItems([...generateDefaultItems(), ...generateOrgItems()]);
        setBottomItems(generateBottomItems());
    }, [generateDefaultItems, generateOrgItems, generateBottomItems]);

    return { topItems, bottomItems };
}
